import React, {useMemo, useState} from "react";
import TableContainer from "../../../components/Common/TableContainer";
import DeleteModal from "../../../components/Common/DeleteModal";
import usePermissions from "src/hooks/usePermissions";
import {Button} from "reactstrap";
import ToggleButton from "./ToggleButton";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {IExo} from "../../../store/exo/types";
import {ExoService} from "../../../services/exo.service";

interface TableExoProps {
    data: IExo[]
    pageSize: number
    onPageSizeChange?: (newPageSize: number) => void
    onSearchChange?: (search: string) => void
    onSortChange?: (sortKey?: string, sortOrder?: string) => void

    onUpdate?: (tgBotId: number) => void
    onRemove?: (tgBotId: number) => void
}

const customSort = [{id: "id", desc: true}];

const TableExo: React.FC<TableExoProps> = ({
                                               data,
                                               pageSize,
                                               onPageSizeChange,
                                               onSearchChange,
                                               onSortChange,
                                               onUpdate,
                                               onRemove
                                           }) => {
    const queryClient = useQueryClient();
    const {hasPermission} = usePermissions();

    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);

    const [togglingIds, setTogglingIds] = useState<number[]>([]);

    const {mutate: toggleExo} = useMutation({
        mutationFn: (id: number) => ExoService.toggleById(id),
        onMutate: (id) => setTogglingIds(prev => [...prev, id]),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["keyExoList"]}),
        onSettled: (_, __, id) => setTogglingIds(prev => prev.filter(x => x !== id)),
    });

    const handleToggleClick = (exoData: IExo) => {
        toggleExo(exoData.id);
    };

    const handleUpdateClick = (exoData: IExo) => {
        if (onUpdate) onUpdate(exoData.id)
    }

    const handleRemoveClick = (exoData: IExo) => {
        setDeleteModal(true);
        setDeleteId(exoData.id);
    }

    const handleSureDeleteClick = () => {
        if (deleteId && onRemove) onRemove(deleteId)

        setDeleteModal(false);
        setDeleteId(null);
    }

    const baseColumn = {
        filterable: true,
        disableFilters: true,
        sortable: true,
    };

    const columns = useMemo(
        () => [
            {
                Header: "ID",
                accessor: "id",
                ...baseColumn,
            },
            {
                Header: "Name",
                accessor: "name",
                ...baseColumn,
            },
            {
                Header: "Description",
                Cell: (cellProps: any) => {
                    const row: IExo = cellProps.row.original;

                    if (!row.description)
                        return <em className="text-muted">Empty</em>
                    return row.description;
                },
            },
            {
                Header: "Type",
                accessor: "type",
                ...baseColumn
            },
            {
                Header: "Enabled",
                Cell: (cellProps: any) => {
                    const row: IExo = cellProps.row.original;

                    return (
                        <ToggleButton
                            checked={row.status}
                            loading={togglingIds.includes(row.id)}
                            onClick={() => handleToggleClick(row)}
                            disabled={!hasPermission("edit-echo-notifications")}
                        />
                    );
                },
            },
            {
                Header: "Action",
                Cell: (cellProps: any) => {
                    return (
                        <div className="d-flex gap-3">
                            {hasPermission("edit-echo-notifications") &&
                                <Button color="link" className="text-success p-0"
                                        onClick={() => handleUpdateClick(cellProps.row.original)}>
                                    <i className="mdi mdi-pencil font-size-18"></i>
                                </Button>}

                            {hasPermission("delete-echo-notifications") &&
                                <Button color="link" className="text-danger p-0"
                                        onClick={() => handleRemoveClick(cellProps.row.original)}>
                                    <i className="mdi mdi-delete font-size-18"></i>
                                </Button>}
                        </div>
                    );
                },
            },
        ],
        [hasPermission]
    );

    return (
        <>
            <TableContainer
                columns={columns}
                data={data}
                isGlobalFilter={true}
                customPageSize={pageSize}
                className="table align-middle datatable dt-responsive table-check nowrap"
                onPageSizeChange={onPageSizeChange}
                onSearchChange={onSearchChange}
                onSortChange={onSortChange}
                customSort={customSort}
            />

            {deleteModal && <DeleteModal
                show={deleteModal}
                onDeleteClick={handleSureDeleteClick}
                onCloseClick={() => setDeleteModal(false)}
            />}
        </>
    )
}

export default React.memo(TableExo)